import React, { useMemo, useRef, useState } from "react";
import { X, Upload, FileSpreadsheet, Download, ArrowRight, ArrowLeft, Check, AlertTriangle, Users, Building2, Briefcase, ListChecks, FolderKanban, UserRound } from "lucide-react";
import { api, API, apiErrorMessage } from "../../api";

/**
 * Reusable 3-step CSV import wizard.
 *
 * Props:
 *   entity       — "team" | "tasks" | "roles" | "departments" | "clients" | "projects"
 *   open, onClose, onImported(result)
 *   toast?       — optional toast helper
 */
const ENTITY_META = {
  team:        { label: "Team members", icon: Users,        blurb: "Import people with roles, capacity and rates." },
  tasks:       { label: "Tasks",        icon: ListChecks,   blurb: "Import your activity/task library with unit costs." },
  roles:       { label: "Roles",        icon: UserRound,    blurb: "Import job titles / seniorities used to tag people." },
  departments: { label: "Departments",  icon: Building2,    blurb: "Import departments used to group people." },
  clients:     { label: "Clients",      icon: Briefcase,    blurb: "Import your client list with brand colour (optional)." },
  projects:    { label: "Projects",     icon: FolderKanban, blurb: "Import projects with budgets, rates and opening balances." },
};

const REQUIRED_COLS = {
  roles: ["name"],
  departments: ["name"],
  clients: ["name"],
  tasks: ["name"],
  team: ["email"],
  projects: ["name", "client_name"],
};

const OPTIONAL_COLS = {
  roles: [],
  departments: [],
  clients: ["color"],
  tasks: ["default_cost", "default_billable"],
  team: [
    "first_name", "last_name", "name", "role", "employee_id",
    "timezone", "capacity_hours", "weekly_target_hours",
    "billable_rate", "cost_rate",
  ],
  projects: ["budget_hours", "rate", "billable", "opening_hours", "opening_revenue"],
};

const NOTES = {
  team: [
    "role: `member` or `admin` (defaults to member)",
    "billable_rate and cost_rate are stored as today's rate; edit history from the member drawer.",
    "New emails create placeholder users — send invites via the option below to grant them login access.",
  ],
  projects: [
    "client_name must match an existing client — import Clients first if needed.",
    "opening_hours and opening_revenue capture historical work done before this import (they add to spent hours / revenue).",
  ],
  tasks: ["default_billable: true/false (defaults to true)."],
  clients: ["color: hex code like #22C55E (defaults to green)."],
  roles: [],
  departments: [],
};

export default function ImportWizard({ entity, open, onClose, onImported, toast }) {
  const meta = ENTITY_META[entity];
  const [step, setStep] = useState(1); // 1 upload, 2 preview, 3 result
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [onDup, setOnDup] = useState("skip");
  const [inviteMode, setInviteMode] = useState("none");
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  if (!open || !meta) return null;

  const reset = () => {
    setStep(1); setCsvText(""); setFileName(""); setPreview(null);
    setErr(""); setResult(null); setOnDup("skip"); setInviteMode("none");
  };
  const close = () => { reset(); onClose?.(); };

  const readFile = (file) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const doPreview = async () => {
    if (!csvText.trim()) { setErr("Pick a CSV file first."); return; }
    setBusy(true); setErr("");
    try {
      const p = await api.importPreview(entity, csvText);
      setPreview(p);
      setStep(2);
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't parse that CSV."));
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true); setErr("");
    try {
      const body = { csv_text: csvText, on_duplicate: onDup };
      if (entity === "team") body.invite_mode = inviteMode;
      const r = await api.importCommit(entity, body);
      setResult(r);
      setStep(3);
      onImported?.(r);
      toast?.({ title: `${meta.label} imported`,
                description: `${r.created} created · ${r.updated} updated · ${r.skipped} skipped` });
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Import failed."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid={`import-wizard-${entity}`}>
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-cream flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-cream-2/60"><FileSpreadsheet size={18} className="text-forest" /></div>
            <div>
              <div className="font-display text-xl leading-tight">Import {meta.label.toLowerCase()}</div>
              <div className="text-xs text-[#6a736f]">Step {step} of 3 · CSV</div>
            </div>
          </div>
          <button onClick={close} className="text-[#6a736f]" data-testid={`import-close-${entity}`}>
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === 1 && (
            <StepUpload
              entity={entity} meta={meta}
              csvText={csvText} setCsvText={setCsvText}
              fileName={fileName} fileRef={fileRef}
              readFile={readFile}
              err={err}
            />
          )}
          {step === 2 && (
            <StepPreview
              entity={entity} preview={preview}
              onDup={onDup} setOnDup={setOnDup}
              inviteMode={inviteMode} setInviteMode={setInviteMode}
              err={err}
            />
          )}
          {step === 3 && (
            <StepResult entity={entity} result={result} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-cream bg-white flex items-center justify-between gap-3">
          <div className="text-xs text-[#6a736f]">
            {step === 1 && (
              <a href={api.importTemplateUrl(entity)}
                 download
                 data-testid={`import-download-template-${entity}`}
                 className="inline-flex items-center gap-1 hover:text-forest">
                <Download size={12} /> Download sample CSV
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)}
                      data-testid={`import-back-${entity}`}
                      className="btn-outline-forest inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {step < 3 && (
              <button
                onClick={step === 1 ? doPreview : doCommit}
                disabled={busy || (step === 1 && !csvText.trim()) || (step === 2 && preview?.rows?.length === 0)}
                data-testid={`import-${step === 1 ? "preview" : "commit"}-${entity}`}
                className="btn-coral inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
              >
                {step === 1
                  ? <>{busy ? "Analysing…" : "Preview import"} <ArrowRight size={14} /></>
                  : <>{busy ? "Importing…" : "Confirm & import"} <Check size={14} /></>}
              </button>
            )}
            {step === 3 && (
              <button onClick={close}
                      data-testid={`import-done-${entity}`}
                      className="btn-coral inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium">
                Done <Check size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepUpload({ entity, meta, csvText, fileName, fileRef, readFile, err }) {
  const req = REQUIRED_COLS[entity];
  const opt = OPTIONAL_COLS[entity];
  const notes = NOTES[entity] || [];
  return (
    <div className="space-y-5" data-testid="import-step-upload">
      <div>
        <div className="text-sm text-[#3f4a44]">{meta.blurb}</div>
      </div>

      <div className="bg-cream-2/40 border border-cream rounded-lg p-4 space-y-2">
        <div className="text-sm font-semibold text-forest">CSV format</div>
        <div className="text-xs text-[#3f4a44]">
          <div><b>Required columns:</b> <span className="font-mono">{req.join(", ")}</span></div>
          {opt.length > 0 && (
            <div className="mt-1"><b>Optional:</b> <span className="font-mono">{opt.join(", ")}</span></div>
          )}
        </div>
        {notes.length > 0 && (
          <ul className="text-xs text-[#6a736f] list-disc pl-4 mt-1 space-y-0.5">
            {notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </div>

      <div
        className="border-2 border-dashed border-cream rounded-lg p-8 text-center cursor-pointer hover:bg-cream-2/30"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) readFile(f);
        }}
        data-testid="import-dropzone"
      >
        <Upload size={22} className="mx-auto text-forest mb-2" />
        <div className="text-sm font-medium">
          {fileName ? <>Selected <span className="font-mono">{fileName}</span></> : "Drop your CSV here or click to browse"}
        </div>
        <div className="text-xs text-[#6a736f] mt-1">UTF-8, comma-separated. First row must be the header.</div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
               onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); }}
               data-testid="import-file-input" />
      </div>

      {csvText && (
        <div>
          <div className="text-xs font-semibold text-[#3f4a44] mb-1">Preview (first 4 lines)</div>
          <pre className="bg-cream-2/40 border border-cream rounded-md p-3 text-xs overflow-x-auto max-h-40"
               data-testid="import-csv-preview">
            {csvText.split("\n").slice(0, 4).join("\n")}
          </pre>
        </div>
      )}

      {err && <div className="text-sm text-red-600" data-testid="import-error">{err}</div>}
    </div>
  );
}

function StepPreview({ entity, preview, onDup, setOnDup, inviteMode, setInviteMode, err }) {
  if (!preview) return null;
  const { summary, rows, warnings } = preview;
  const shown = rows.slice(0, 100);
  const badge = (a) => ({
    create: "bg-green-100 text-green-800",
    update: "bg-blue-100 text-blue-800",
    skip:   "bg-cream-2 text-[#6a736f]",
  })[a];

  return (
    <div className="space-y-5" data-testid="import-step-preview">
      <div className="grid grid-cols-4 gap-3">
        {[
          { k: "total",  label: "Rows",     testid: "sum-total" },
          { k: "create", label: "Create",   testid: "sum-create" },
          { k: "update", label: "Update",   testid: "sum-update" },
          { k: "skip",   label: "Skip",     testid: "sum-skip" },
        ].map((t) => (
          <div key={t.k} className="border border-cream rounded-lg p-3 text-center" data-testid={t.testid}>
            <div className="text-2xl font-display">{summary[t.k] ?? 0}</div>
            <div className="text-xs text-[#6a736f]">{t.label}</div>
          </div>
        ))}
      </div>

      {warnings?.length > 0 && (
        <div className="border border-[#F5D77A] bg-[#FFF8E1] rounded-lg p-3 text-sm text-[#5A4A00] flex items-start gap-2"
             data-testid="import-warnings">
          <AlertTriangle size={14} className="mt-0.5" />
          <div>{warnings.join(" · ")}</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-sm border border-cream rounded-lg p-3">
          <span className="text-xs font-semibold text-[#3f4a44]">If a row already exists</span>
          <select value={onDup} onChange={(e) => setOnDup(e.target.value)}
                  data-testid="import-on-duplicate"
                  className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
            <option value="skip">Skip existing rows</option>
            <option value="update">Update existing rows with CSV values</option>
          </select>
        </label>

        {entity === "team" && (
          <label className="block text-sm border border-cream rounded-lg p-3">
            <span className="text-xs font-semibold text-[#3f4a44]">For new team members</span>
            <select value={inviteMode} onChange={(e) => setInviteMode(e.target.value)}
                    data-testid="import-invite-mode"
                    className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
              <option value="none">Create as placeholders (no invite)</option>
              <option value="invite">Also create pending invites</option>
            </select>
          </label>
        )}
      </div>

      <div className="border border-cream rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
            <tr className="text-left">
              <th className="px-3 py-2 w-14">Row</th>
              <th className="px-3 py-2 w-24">Action</th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {shown.map((r) => (
              <tr key={r.row_num} data-testid={`import-row-${r.row_num}`}>
                <td className="px-3 py-2 font-mono text-xs text-[#6a736f]">{r.row_num}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge(r.action)}`}
                        data-testid={`import-row-action-${r.row_num}`}>
                    {r.action}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {summariseRow(entity, r.data)}
                </td>
                <td className="px-3 py-2 text-xs text-[#6a736f]">{r.reason || ""}</td>
              </tr>
            ))}
            {rows.length > shown.length && (
              <tr><td colSpan={4} className="px-3 py-2 text-xs text-[#6a736f]">
                Showing first {shown.length} of {rows.length} rows — the full file will be imported.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="import-error">{err}</div>}
    </div>
  );
}

function StepResult({ entity, result }) {
  if (!result) return null;
  return (
    <div className="space-y-4" data-testid="import-step-result">
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-start gap-2">
        <Check size={16} className="mt-0.5" />
        <div>
          <div className="font-semibold">Import complete</div>
          <div className="text-xs mt-0.5">
            {result.created} created · {result.updated} updated · {result.skipped} skipped
          </div>
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div className="border border-cream rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-cream-2/50 text-xs font-semibold">Skipped / errored rows</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#EAF3EE]">
              {result.errors.slice(0, 100).map((e, i) => (
                <tr key={i} data-testid={`import-error-row-${e.row}`}>
                  <td className="px-3 py-2 font-mono text-xs w-14">{e.row}</td>
                  <td className="px-3 py-2 text-xs text-[#6a736f]">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entity === "team" && result.invites?.length > 0 && (
        <div className="border border-cream rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-cream-2/50 text-xs font-semibold">Pending invites created</div>
          <ul className="divide-y divide-[#EAF3EE]">
            {result.invites.map((inv, i) => (
              <li key={i} className="px-3 py-2 text-xs" data-testid={`import-invite-${inv.email}`}>
                <span className="font-mono">{inv.email}</span> — share the invite link from the Team page.
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function summariseRow(entity, d) {
  if (!d) return "";
  if (entity === "roles" || entity === "departments") return d.name || "—";
  if (entity === "clients") return `${d.name || "—"}${d.color ? `  ·  ${d.color}` : ""}`;
  if (entity === "tasks") return `${d.name || "—"}  ·  $${Number(d.default_cost || 0).toFixed(2)}  ·  ${d.default_billable ? "billable" : "internal"}`;
  if (entity === "team") return `${d.email || "—"}  ·  ${d.name || ""}  ·  ${d.role || "member"}${d.billable_rate ? `  ·  billable $${d.billable_rate}` : ""}${d.cost_rate ? `  ·  cost $${d.cost_rate}` : ""}`;
  if (entity === "projects") return `${d.name || "—"}  →  ${d.client_name || "—"}${d.budget_hours ? `  ·  ${d.budget_hours}h` : ""}${d.rate ? `  ·  $${d.rate}/h` : ""}${d.opening_hours ? `  ·  opening ${d.opening_hours}h` : ""}`;
  // fallback
  return Object.entries(d).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join("  ·  ");
}
